import React, { FC, memo, useEffect, useState } from "react";
import {
    IAddCustomerInformationRequest,
    IShippingPage,
    IUpdateShippingInformationRequest
} from "@montana/models/generated/cms.interface";
import { CheckoutB2CBase, CheckoutSteps } from "../checkout-b2c-base";
import { ShippingOptionsForm } from "./shipping-options-form/shipping-options-form.component";
import { useCheckout } from "@montana/containers/checkout";
import { IShippingOption } from "./shipping-options-form/shipping-option";
import { useTranslations } from "@montana/containers/translations";
import { useCart } from "@montana/containers/cart";
import { useTracking } from "@montana/containers/tracking";
import {useConnectedServiceFunctions} from '@montana/shared/use-connected-service-functions';
import {getCountries} from '@montana/services/checkout';
import {IFieldOptionValue} from '@montana/shared/form/field/field';
import {useSettings} from '@montana/containers/settings/settings.container';

export const ShippingInformationPage: FC<IShippingPage> = memo((props: IShippingPage) => {
    const [shippingOptions, setShippingOptions] = useState<IShippingOption[]>([]);
    const [countries, setCountries] = useState<IFieldOptionValue[]>([]);
    const [ctaText, setCtaText] = useState('');
    const [submitForm, setSubmitForm] = useState(false);
    const translate = useTranslations();
    const { trackCheckoutStep } = useTracking();
    const { market } = useSettings();
    const marketId = market?.marketId ?? '';

    const {
        loadShippingInformation,
        shippingInformationLoaded,
        shippingInformation,
        setShippingInformation,
        loadCustomerInformation,
        setCustomerInformation,
    } = useCheckout();

    const [cGetCountries] = useConnectedServiceFunctions(getCountries);

    const { cartGuid } = useCart();

    useEffect(() => {
        trackCheckoutStep(2);

        setCtaText(translate(t => t.checkoutPage.shippingInformationProceedToPayment, 'Proceed to Payment method'));

        cGetCountries(marketId)
            .then(response => {
                if (response.ok) {
                    response.json().then(countries => {
                        const countryOptions = Object.entries(countries).
                        map<IFieldOptionValue>(([key, value]): IFieldOptionValue => ({ value: key, text: `${value}` }));
                        setCountries(countryOptions);
                    });
                } else {
                    console.error('Failed to get countries');
                }
            })
            .catch(error => console.error('Failed to get countries', error));
    }, []);

    useEffect(() => {
        if (cartGuid) {
            loadShippingInformation();
            loadCustomerInformation();
        }
    }, [cartGuid]);

    useEffect(() => {
        if (shippingInformationLoaded) {
            const options: IShippingOption[] = [];

            shippingInformation.forEach(shipping => {
                if (shipping.isHomeDelivery) {
                    options.push({
                        dispatchTime: shipping.deliveryTime,
                        info: shipping.title,
                        price: shipping.price,
                        type: 'homeDelivery',
                        homeDeliveryOptions: shipping.shippingAgentServiceCodes,
                        deliveryId: shipping.deliveryId
                    });
                } else {
                    options.push({
                        dispatchTime: shipping.deliveryTime,
                        info: shipping.title,
                        price: shipping.price,
                        type: 'parcelShop',
                        deliveryId: shipping.deliveryId
                    });
                }
            });


            setShippingOptions(options);
        }
    }, [shippingInformationLoaded]);

    useEffect(() => {
        if (submitForm) {
            setSubmitForm(value => !value);
        }
    }, [submitForm]);

    function handleOnSubmit(shippingInformation: IUpdateShippingInformationRequest, customerInformation: IAddCustomerInformationRequest) {
        setCustomerInformation(customerInformation);
        setShippingInformation(shippingInformation, true);
    }

    return (
        <CheckoutB2CBase
            {...props}
            step={CheckoutSteps.Delivery}
            ctaText={ctaText}
            onCTAClick={() => setSubmitForm(true)}
            showStepper
        >
            {
                <ShippingOptionsForm
                    options={shippingOptions}
                    onSubmit={handleOnSubmit}
                    submitForm={submitForm}
                    countries={countries}
                    newsletterSignupAcceptance={props.newsletterSignupAcceptance}
                    termsAndPrivacyPolicy={props.termsAndPrivacyPolicy}
                />
            }
        </CheckoutB2CBase>
    )
});
